import { memo, useCallback, useEffect, useState } from "react";
import MaterialIcon from "../../../../components/BuilderComponents/MaterialIcon";
import Label from "../../../../components/FormComponents/Label";
import Toggle from "../../../../components/FormComponents/Toggle";
import { SCREEN_WIDTHS } from "../../../../constant/constant";
import { useAppBlocksContext } from "../../../../context/AppBlocksContext";
import useZTheme from "../../../../hooks/useZTheme";
import { elementConfig } from "../../../../utils/Config/DefaultConfig/elementConfig";
import { getSidebarElementsForPositionRecordview } from "../../../../utils/sidebarElementList";
import PositionScreenWithEdit from "../../../FlexibleLayout/PositionScreenWithEdit";
import {
  findItemById,
  getOffset,
  getPositionedElementConfig,
  updateOnlyElementConfigInLayout
} from "../../../FlexibleLayout/utils";
import DetailsviewElementsBar from "../../DetailsViewRenderer/Desktop/DetailsviewElementsBar";
import { HiddenColumns } from "../Mobile/MobileRecordEditSection";
import {
  ColumnPreFiltersWithSummary,
  // EditCustomColumn,
  EditPrefilterWithSummary,
  EditPreSortWithSummary,
  RVOnRowAction
} from "./NewDesktopRecordviewRenderer";
import RecordviewPageActionsHandler from "./RecordviewPageActionsHandler";

const PositionedDesktopRecordview = () => {
  useZTheme("desktop");
  const { blockData, blockDataDispatch } = useAppBlocksContext();
  const [layoutConfig, setLayoutConfig] = useState([]);
  const [cardDimentions, setCardDimentions] = useState({
    width: 0
  });
  const initialCatalogState = {
    show: false,
    tableElementId: "",
    columnId: null
  };
  const [isCatalogPlaygroundOpen, setIsCatalogPlaygroundOpen] = useState(initialCatalogState);

  const openCatalogPlayground = (id, columnId) => {
    const tableElement = findItemById(blockData.position_block_config, id);
    if (tableElement.config.type === "map") {
      setCardDimentions({ width: tableElement.config.card_width });
      setLayoutConfig(tableElement.config.card.layout);
      setIsCatalogPlaygroundOpen({ show: true, tableElementId: id, columnId: null });
      return;
    }
    if (!tableElement || tableElement.config.type !== "rv_table") return;
    if (columnId) {
      const column = tableElement.config.columns.find((c) => c.id === columnId);
      const columnWidth = parseInt(column.width_value) || 200;
      setCardDimentions({ width: columnWidth });
      setLayoutConfig(column.layout_config.layouts || []);
      setIsCatalogPlaygroundOpen({ show: true, tableElementId: id, columnId });
    } else {
      // total gap calculation
      const gapCount = tableElement.config.catalog.cards_per_row - 1;
      const totalGap = gapCount * parseInt(tableElement.config.catalog.gap_between_cards);

      // remove gap from total width
      const totalWidth = parseInt(tableElement.style.width) - totalGap;

      // calculate card width and height
      const cardWidth = Math.floor(totalWidth / tableElement.config.catalog.cards_per_row);

      setCardDimentions({ width: cardWidth });
      setLayoutConfig(tableElement.config.catalog.layout);
      setIsCatalogPlaygroundOpen({ show: true, tableElementId: id, columnId: null });
    }
  };

  useEffect(() => {
    if (!isCatalogPlaygroundOpen.show || !layoutConfig) return;

    // Find the table element in the latest blockData
    const tableElement = findItemById(blockData.position_block_config, isCatalogPlaygroundOpen.tableElementId);
    const tableConfig = JSON.parse(JSON.stringify(tableElement.config));
    if (tableElement.config.type === "map") {
      tableConfig.card = tableConfig.card || {};
      tableConfig.card.layout = layoutConfig;
      const newLayoutConfig = updateOnlyElementConfigInLayout(
        blockData.position_block_config,
        isCatalogPlaygroundOpen.tableElementId,
        tableConfig
      );
      blockDataDispatch({ type: "UPDATE_POSITION_BLOCK_CONFIG", payload: newLayoutConfig });
      return;
    }
    if (!tableElement || tableElement.config.type !== "rv_table") return;

    if (isCatalogPlaygroundOpen.columnId) {
      const columnIndex = tableElement.config.columns.findIndex((c) => c.id === isCatalogPlaygroundOpen.columnId);
      tableConfig.columns[columnIndex].layout_config.layouts = layoutConfig;
      const newLayoutConfig = updateOnlyElementConfigInLayout(
        blockData.position_block_config,
        isCatalogPlaygroundOpen.tableElementId,
        tableConfig
      );
      blockDataDispatch({ type: "UPDATE_POSITION_BLOCK_CONFIG", payload: newLayoutConfig });
    } else {
      tableConfig.catalog = tableConfig.catalog || {};
      tableConfig.catalog.layout = layoutConfig;

      const newLayoutConfig = updateOnlyElementConfigInLayout(
        blockData.position_block_config,
        isCatalogPlaygroundOpen.tableElementId,
        tableConfig
      );
      blockDataDispatch({ type: "UPDATE_POSITION_BLOCK_CONFIG", payload: newLayoutConfig });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutConfig, isCatalogPlaygroundOpen.show]);

  const closeCatalogPlayground = () => {
    setIsCatalogPlaygroundOpen(initialCatalogState);
  };

  return (
    <div>
      {!isCatalogPlaygroundOpen.show && <RVTableViewWithActions openCatalogPlayground={openCatalogPlayground} />}
      {isCatalogPlaygroundOpen.show && (
        <>
          <RVCAtalogViewForPostioned
            width={cardDimentions.width}
            height={"100%"}
            layoutConfig={layoutConfig}
            setLayoutConfig={setLayoutConfig}
            device={SCREEN_WIDTHS[blockData.device]}
            tableId={blockData.config.layout.table_id}
            tableType={blockData.table_type}
            elements={[blockData]}
            onClose={closeCatalogPlayground}
          />
        </>
      )}
    </div>
  );
};

const RVCAtalogViewForPostioned = ({
  width,
  height,
  layoutConfig,
  setLayoutConfig,
  device,
  tableId,
  tableType,
  elements,
  onClose,
  targetFolderPath = []
}) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const handleDragStart = (e, name, id, type) => {
    type = type === "empty" ? "field" : type;
    let newConfig = getPositionedElementConfig(type, id);
    if (newConfig.type === "element") {
      if (type === "activitycard" || type === "activity-recordsview" || type === "progress-bar") {
        newConfig.config = {
          type,
          id
        };
      } else {
        newConfig.config = elementConfig[type === "tableview" ? "table" : type];
      }
    }

    const offset = getOffset(e);
    setDraggedItem(newConfig);
    setDragOffset(offset);
  };
  return (
    <>
      {onClose && (
        <div className="position_relative">
          <MaterialIcon name="arrow_back" className="cursor_pointer position_absolute p_3" onClick={onClose} />
        </div>
      )}
      <div className="detailsView_outer_body">
        <div style={{ height: height }}>
          <PositionScreenWithEdit
            layoutConfig={layoutConfig || []}
            setLayoutConfig={setLayoutConfig}
            draggedItem={draggedItem}
            setDragOffset={setDragOffset}
            setDraggedItem={setDraggedItem}
            dragOffset={dragOffset}
            type={"editable"}
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
            gridSize={6}
            screenWidth={width}
            tableId={tableId}
            tableType={tableType}
            device={device}
            buttonElements={elements}
            jobPlaces={"records_view_row"}
            targetFolderPath={targetFolderPath}
          />
        </div>
        <DetailsviewElementsBar handleDragStart={handleDragStart} isPositionedScreen={true} />
      </div>
    </>
  );
};
const filteredSidebarElements = getSidebarElementsForPositionRecordview().map((group) => ({
  ...group,
  items: group.items.filter((item) => item.type !== "rv_actions_list")
}));
const RVTableViewWithActions = ({ openCatalogPlayground }) => {
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const { blockData } = useAppBlocksContext();
  const { device } = blockData;

  const handleDragStart = useCallback(
    (e, item) => {
      const { type } = item;
      let newConfig = getPositionedElementConfig(type, device);
      if (newConfig.type === "element") {
        newConfig.config = elementConfig[type];
      }
      const offset = getOffset(e);
      setDraggedItem(newConfig);
      setDragOffset(offset);
    },
    [device]
  );

  return (
    <div className="detailsView_outer_body">
      <div className="screen-center-wrapper">
        <RecordviewPageActionsHandler />
        <PositionPlayground
          dragOffset={dragOffset}
          setDragOffset={setDragOffset}
          setDraggedItem={setDraggedItem}
          draggedItem={draggedItem}
          openCatalogPlayground={openCatalogPlayground}
        />
      </div>

      <div className="rightsidebar" style={{ padding: "18px" }}>
        <RvElementBar data={filteredSidebarElements} onDragStart={handleDragStart} />
      </div>
    </div>
  );
};

const PositionPlayground = ({ dragOffset, setDragOffset, draggedItem, setDraggedItem, openCatalogPlayground }) => {
  const { blockData, blockDataDispatch } = useAppBlocksContext();

  const { pre_filters, default_sort, hidden_columns, column_pre_filters, on_row_action } =
    blockData?.config?.layout || {};

  const [toggleRvBlock, setToggleRvBlock] = useState({
    preFilters: !!pre_filters?.length,
    preSort: !!default_sort?.length,
    hiddenColumns: !!hidden_columns?.length,
    columnPreFilters: !!column_pre_filters?.length,
    onRowClickJob: !!on_row_action?.job_id
  });
  const { position_block_config: blockLayoutConfig, device } = blockData;
  // const [screenLayoutInfo, setScreenLayoutInfo] = useState({
  //   showGrid: true,
  //   showTree: false,
  //   showTemplate: false,
  //   screenMode: "editable",
  //   scaleFactor: 1,
  //   gridSize: (() => {
  //     if (device === "desktop") {
  //       return 8;
  //     } else {
  //       return blockData.position_block_metadata?.grid_size || 4;
  //     }
  //   })()
  // });

  const [selectedItem, setSelectedItem] = useState(null);

  const [layoutConfig, setLayoutConfig] = useState(JSON.parse(JSON.stringify(blockLayoutConfig)));

  const openEditBar = (selectedItem, otherInfo) => {
    const element = findItemById(layoutConfig, selectedItem.id);
    if (!element) return;
    let { id, config = {} } = element;
    if (config.type === "rv_table" && otherInfo) {
      if (otherInfo.type === "add_field_rich_column") {
        openCatalogPlayground(id, otherInfo.config.id);
        return;
      }
      if (otherInfo.type === "edit_catalog") {
        openCatalogPlayground(id);
        return;
      }
    }
    if (otherInfo && otherInfo.type === "edit_map_card") {
      openCatalogPlayground(id);
      return;
    }
  };

  const handleChangeToggleBlock = (stateName, value) => {
    setToggleRvBlock({ ...toggleRvBlock, [stateName]: value });
  };

  // const handleChangeLayoutInfo = (type, value) => {
  //   setScreenLayoutInfo({ ...screenLayoutInfo, [type]: value });
  // };

  useEffect(() => {
    blockDataDispatch({ type: "UPDATE_POSITION_BLOCK_CONFIG", payload: JSON.parse(JSON.stringify(layoutConfig)) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutConfig]);

  return (
    <>
      <div className="page-action-bar-top space_between">
        <ToggleRVConfigPanel handleChangeToggleBlock={handleChangeToggleBlock} toggleRvBlock={toggleRvBlock} />
        {/* <PositionScreenActionBar
          screenLayoutInfo={screenLayoutInfo}
          setScreenLayoutInfo={setScreenLayoutInfo}
          device={"desktop"}
        /> */}
      </div>
      <div className="position-screen-center-page">
        <RVConfigPanel showPanel={toggleRvBlock} />
        <>
          <PositionScreenWithEdit
            layoutConfig={layoutConfig || []}
            setLayoutConfig={setLayoutConfig}
            draggedItem={draggedItem}
            setDragOffset={setDragOffset}
            setDraggedItem={setDraggedItem}
            dragOffset={dragOffset}
            // type={screenLayoutInfo.showTemplate ? "view" : screenLayoutInfo.screenMode}
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
            // gridSize={screenLayoutInfo.gridSize}
            screenWidth={SCREEN_WIDTHS[device]}
            // showTree={screenLayoutInfo.showTree}
            // setShowTree={() => setScreenLayoutInfo({ ...screenLayoutInfo, showTree: false })}
            // showGrid={screenLayoutInfo.showGrid}
            tableId={blockData?.config?.layout?.table_id || ""}
            tableType={blockData?.table_type || ""}
            device={device}
            buttonElements={[blockData]}
            onClick={openEditBar}
            // scaleFactor={screenLayoutInfo.scaleFactor}
            jobPlaces={"screen"}
          />
          {/* <BuilderModal
            isOpen={screenLayoutInfo.showTemplate}
            onClose={() => handleChangeLayoutInfo("showTemplate", false)}
            modalName="Templates"
            customStyleClsName="template_modal_fullsize"
            bodyStyle={{ minHeight: "100vh", overflow: "auto" }}
          >
            <PositionedTemplateList />
          </BuilderModal> */}
        </>
      </div>
    </>
  );
};

const RVConfigPanel = ({ showPanel }) => {
  const { blockData, blockDataDispatch } = useAppBlocksContext();
  const handleChangePrefilters = (type, data) => {
    if (type === "prefilter") {
      blockDataDispatch({ type: "PRE_FILTERS", payload: data });
    }
    if (type === "owner_pre_filters") {
      blockDataDispatch({ type: "OWNER_PRE_FILTERS", payload: data });
    }
  };
  const handleChangeDefaultSort = (data) => {
    blockDataDispatch({ type: "DEFAULT_SORT", payload: data });
  };
  const handleChangeHiddenColumn = (column) => {
    blockDataDispatch({ type: "HIDDEN_COLUMNS", payload: column });
  };
  const handleChangeOnRowAction = (data) => {
    blockDataDispatch({ type: "ON_ROW_ACTION", payload: data });
  };
  return (
    <div className="rv_prefilter_sort_container">
      {showPanel.preFilters && (
        <div className="position_relative">
          <Label
            className="position_absolute font_weight_semibold"
            style={{
              top: "-12px",
              left: "20px",
              padding: "4px",
              display: blockData?.config?.layout?.pre_filters?.length > 0 ? "none" : "block"
            }}
          >
            Pre Filters
          </Label>
          <EditPrefilterWithSummary
            prefiltersWithFormula={{
              prefilter: blockData?.config?.layout?.pre_filters || [],
              prefilters_joined_by: blockData?.config?.layout?.prefilters_joined_by || "",
              owner_pre_filters: blockData?.config?.layout?.owner_pre_filters || {}
            }}
            handleChange={handleChangePrefilters}
            tableId={blockData?.config?.layout?.table_id || ""}
            activityId={blockData?.config?.layout?.activity_id || ""}
            tableType={blockData?.table_type || ""}
          />
        </div>
      )}
      {showPanel.preSort && (
        <div className="position_relative">
          <Label
            className="position_absolute font_weight_semibold"
            style={{
              top: "-12px",
              left: "20px",
              padding: "4px",
              display: blockData?.config?.layout?.default_sort?.length > 0 ? "none" : "block"
            }}
          >
            Pre Sort
          </Label>
          <EditPreSortWithSummary
            defaultSort={blockData?.config?.layout.default_sort || []}
            handleChangeDefaultSort={handleChangeDefaultSort}
            tableId={blockData?.config?.layout?.table_id || ""}
            activityId={blockData?.config?.layout?.activity_id || ""}
            tableType={blockData?.table_type || ""}
          />
        </div>
      )}
      {showPanel.columnPreFilters && (
        <div className="position_relative">
          <Label
            className="position_absolute font_weight_semibold"
            style={{
              top: "-12px",
              left: "20px",
              padding: "4px"
            }}
          >
            Column Pre Filters
          </Label>
          <div className="recordview_summary_container d_flex justify_center">
            <ColumnPreFiltersWithSummary />
          </div>
        </div>
      )}
      {showPanel.onRowClickJob && (
        <div className="position_relative">
          <Label
            className="position_absolute font_weight_semibold"
            style={{ top: "-12px", left: "20px", padding: "4px" }}
          >
            On Row Job
          </Label>
          <RVOnRowAction
            job={blockData?.config?.layout?.on_row_action || null}
            onChange={handleChangeOnRowAction}
            tableId={blockData?.config?.layout?.table_id}
            tableType={blockData?.table_type || "statemachine"}
            device={blockData?.device || "desktop"}
          />
        </div>
      )}
      {showPanel.hiddenColumns && (
        <HiddenColumns
          columns={blockData?.config?.layout?.hidden_columns || []}
          handleChange={handleChangeHiddenColumn}
          tableId={blockData?.config?.layout?.table_id}
        />
      )}
    </div>
  );
};

const ToggleRVConfigPanel = ({ toggleRvBlock, handleChangeToggleBlock }) => {
  const { blockData } = useAppBlocksContext();
  const { pre_filters, default_sort, hidden_columns, column_pre_filters, on_row_action } =
    blockData?.config?.layout || {};
  const listOfBlocks = [
    { name: "Pre Filters", state: "preFilters", count: pre_filters?.length || 0 },
    { name: "Pre Sort", state: "preSort", count: default_sort?.length || 0 },
    { name: "Hidden Columns", state: "hiddenColumns", count: hidden_columns?.length || 0 },
    { name: "Column Pre Filters", state: "columnPreFilters", count: column_pre_filters?.length || 0 },
    { name: "On Row Job", state: "onRowClickJob", count: on_row_action?.job_id ? 1 : 0 }
  ];
  return (
    <div className="page-action-bar-top-toggle-options">
      {listOfBlocks.map(({ name, state, count }) => (
        <div className="d_flex gap_2 align_center" key={name}>
          <div className="d_flex gap_1 justify_center align_center">
            <Label>{name}</Label>
            {count !== 0 && (
              <span
                className="d_flex gap_1 justify_center align_center"
                style={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "100%",
                  backgroundColor: "var(--builder-active)",
                  border: "1px solid var(--builder-border)"
                }}
              >
                {count}
              </span>
            )}
          </div>
          <Toggle value={toggleRvBlock[state]} onChange={(e) => handleChangeToggleBlock(state, e.target.checked)} />
        </div>
      ))}
    </div>
  );
};

const RvElementBar = ({ data, onDragStart }) => {
  const handleDragStart = useCallback(
    (e, item) => {
      onDragStart && onDragStart(e, item);
    },
    [onDragStart]
  );
  return (
    <div>
      {data.map((group) => (
        <SideBarGroup key={group.id} group={group} onDragStart={handleDragStart} />
      ))}
    </div>
  );
};

const SideBarGroup = memo(({ group, onDragStart }) => {
  return (
    <div>
      {group.heading && <label className="builder_label">{group.heading}</label>}
      <ul className="draggable_formField_rightPanel">
        {group.items.map((item) => (
          <SingleListOfGroup key={item.id} item={item} onDragStart={onDragStart} />
        ))}
      </ul>
    </div>
  );
});

const SingleListOfGroup = ({ item, onDragStart }) => {
  return (
    <li
      onDragStart={(e) => onDragStart && onDragStart(e, item)}
      draggable={onDragStart ? true : false}
      className="draggable_input_field"
    >
      {item.icon && <MaterialIcon name={item.icon} />}
      <span className="field_name">{item.name}</span>
    </li>
  );
};

export default PositionedDesktopRecordview;
export { RVCAtalogViewForPostioned, RvElementBar };
